import {
  isGradingContainer,
  NestableAssetDto,
  nestAssetDtos,
} from "@core/componentsV2/PlayerV2/utils";
import { AssetDto } from "@core/typesV2";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { indexBy } from "remeda";

import { useActivityAssetsSourceQuery } from "./useActivityAssetsSourceQuery";
import { useAssetProgressQuery } from "./useAssetProgressQuery";

export type UseActivityAssetsQueryParams = {
  courseId: string;
  activityId: string;
  pageNumber?: number | undefined;
  totalPages: number | undefined;
  learnerId?: string | undefined;
  attempt?: number;
  withProgress: boolean;
  enabled?: boolean;
  filterByProgress?: boolean;
};

export function useActivityAssetsQuery({
  courseId,
  activityId,
  learnerId,
  attempt,
  pageNumber,
  totalPages,
  withProgress,
  enabled = true,
  filterByProgress = false,
}: UseActivityAssetsQueryParams) {
  const { activity, assets, contentVersion } = useActivityAssetsSourceQuery({
    courseId,
    activityId,
    learnerId,
    attempt,
    enabled,
  });

  const { data: assetProgress, isFetched: isAssetProgressFetched } =
    useAssetProgressQuery(
      {
        courseId,
        activityId,
        learnerId,
      },
      { enabled: enabled && withProgress && !!learnerId },
    );

  const pages = activity?.pages ?? [];
  const requestedPage = pageNumber ? pages[pageNumber - 1] : undefined;

  const isReady =
    enabled &&
    !!activity &&
    !!assets &&
    !!(learnerId || pageNumber) &&
    !!totalPages &&
    !!(learnerId || requestedPage) &&
    (!filterByProgress || isAssetProgressFetched);

  const assetsQuery = useQuery({
    queryKey: [
      "assets",
      {
        courseId,
        activityId,
        learnerId,
        attempt,
        contentVersion,
        pageNumber,
        withProgress,
        filterByProgress,
      },
    ],
    enabled: isReady,
    queryFn: isReady
      ? async (): Promise<AssetDto[]> => {
          const assetIds =
            !learnerId && pageNumber
              ? requestedPage?.simpleLayout?.assets ?? []
              : pages.flatMap((page) => page.simpleLayout?.assets ?? []);

          const assetsById = indexBy(assets, (asset) => asset.id);

          const assetProgressById = indexBy(
            assetProgress ?? [],
            ({ assetId }) => assetId,
          );

          const mergedAssets = assetIds
            .map((assetId) => {
              const asset = assetsById[assetId];
              if (!asset) return null;

              const progress = assetProgressById[assetId];

              return withProgress
                ? { ...asset, progress: progress || null }
                : asset;
            })
            .filter(Boolean) as AssetDto[];

          const nestedAssets = assets.filter(
            (asset) => !assetIds.includes(asset.id),
          );

          return withProgress
            ? [
                ...mergedAssets,
                ...nestedAssets.map((asset) => ({
                  ...asset,
                  progress: assetProgressById[asset.id] || null,
                })),
              ]
            : [...mergedAssets, ...nestedAssets];
        }
      : skipToken,
  });

  const assetsWithProgress = useMemo((): AssetDto[] | undefined => {
    const queriedAssets = assetsQuery.data;
    if (!queriedAssets) return undefined;

    const progressById = indexBy(assetProgress ?? [], (p) => p.assetId);

    return queriedAssets.map((asset) => {
      if (!withProgress) {
        return asset;
      }

      return {
        ...asset,
        progress: progressById[asset.id] || null,
      };
    });
  }, [assetsQuery.data, withProgress, assetProgress]);

  const nestedAssets = useMemo((): NestableAssetDto[] | undefined => {
    if (!assetsWithProgress) return undefined;

    const rootAssetIds =
      !learnerId && pageNumber
        ? requestedPage?.simpleLayout?.assets ?? []
        : undefined;

    const nested = nestAssetDtos(assetsWithProgress, rootAssetIds);

    if (!filterByProgress) {
      return nested;
    }

    const assetWithProgressIds = new Set(
      (assetProgress ?? []).map((progress) => progress.assetId),
    );

    // A grading container never carries a learner response of its own, but the
    // nested asset the learner answered always does — keep the whole subtree so
    // the gradebook can render and grade it.
    const hasTrackableDescendant = (asset: NestableAssetDto): boolean =>
      assetWithProgressIds.has(asset.id) ||
      Object.values(asset.slots ?? {}).some((slotAssets) =>
        slotAssets.some(hasTrackableDescendant),
      );

    return nested.filter(
      (asset) =>
        assetWithProgressIds.has(asset.id) ||
        (isGradingContainer(asset) && hasTrackableDescendant(asset)),
    );
  }, [
    assetsWithProgress,
    learnerId,
    pageNumber,
    requestedPage,
    filterByProgress,
    assetProgress,
  ]);

  return {
    ...assetsQuery,
    data: enabled ? nestedAssets : [],
    flatData: enabled ? assetsWithProgress : [],
  };
}
